const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || '/api'}/chat/sessions`;

class ChatSessionsAPI {
  // Create a new chat session
  async createSession(userId, label = null) {
    const response = await fetch(`${API_BASE_URL}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        user_id: userId,
        label: label
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create chat session');
    }
    
    return response.json();
  }

  // Get all chat sessions for a user with pagination
  async getUserSessions(userId, limit = 100, offset = 0) {
    const params = new URLSearchParams({
      user_id: userId.toString(),
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch chat sessions');
    }
    
    return response.json();
  }

  // Get a specific chat session by ID
  async getSession(sessionId) {
    const response = await fetch(`${API_BASE_URL}/${sessionId}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch chat session');
    }
    
    return response.json();
  }

  // Update a chat session (label)
  async updateSession(sessionId, label) {
    const response = await fetch(`${API_BASE_URL}/${sessionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update chat session');
    }
    
    return response.json();
  }

  // Delete a chat session
  async deleteSession(sessionId) {
    const response = await fetch(`${API_BASE_URL}/${sessionId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete chat session');
    }
    
    return response.ok;
  }

  // Get chat session with messages
  async getSessionWithMessages(sessionId) {
    const response = await fetch(`${API_BASE_URL}/${sessionId}/with-messages`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch chat session with messages');
    }
    
    return response.json();
  }

  // Get user chat sessions with messages
  async getUserSessionsWithMessages(userId, limit = 50, offset = 0) {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/user/${userId}/with-messages?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch user sessions with messages');
    }
    
    return response.json();
  }

  // Get recent chat sessions for a user
  async getRecentSessions(userId, days = 7, limit = 10) {
    const params = new URLSearchParams({
      days: days.toString(),
      limit: limit.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/user/${userId}/recent?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch recent sessions');
    }
    
    return response.json();
  }

  // Search chat sessions by label
  async searchSessionsByLabel(userId, label) {
    const params = new URLSearchParams({
      label: label
    });
    
    const response = await fetch(`${API_BASE_URL}/user/${userId}/search?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to search sessions');
    }
    
    return response.json();
  }

  // Get chat session count for a user
  async getSessionCount(userId) {
    const response = await fetch(`${API_BASE_URL}/user/${userId}/count`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch session count');
    }
    
    return response.json();
  }

  // Delete all chat sessions for a user
  async deleteAllUserSessions(userId) {
    const response = await fetch(`${API_BASE_URL}/user/${userId}/all`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete all user sessions');
    }
    
    return response.json();
  }

  // Helper method to generate a session title from first message
  generateSessionTitle(firstMessage, maxLength = 50) {
    if (!firstMessage) return 'New Chat';
    
    // Clean the message and truncate
    const cleanMessage = firstMessage.replace(/[^\w\s]/g, '').trim();
    if (cleanMessage.length <= maxLength) return cleanMessage;
    
    return cleanMessage.substring(0, maxLength - 3) + '...';
  }

  // Helper method to get the last message preview
  getLastMessagePreview(session) {
    if (!session.messages || session.messages.length === 0) {
      return 'No messages yet';
    }
    
    const lastMessage = session.messages[session.messages.length - 1];
    const content = lastMessage.content || '';
    
    // Truncate long messages
    if (content.length <= 100) return content;
    return content.substring(0, 97) + '...';
  }

  // Helper method to format session for display
  formatSessionForDisplay(session) {
    return {
      id: session.id,
      title: session.label || this.generateSessionTitle(session.messages?.[0]?.content),
      lastMessage: this.getLastMessagePreview(session),
      timestamp: session.created_at,
      messageCount: session.messages?.length || 0
    };
  }
}

export default new ChatSessionsAPI();
